import { Injectable } from "@angular/core";
import { Area, Assertion, AssertionIssue, Component, db, SyncableStatus, Zone } from "../../../core/database/db";
import { AreaWithDetails } from "../../jobs/services/bq-inspection-data.service";
import { AssertionService } from "../../assertions/services/assertion.service";
import { AreaConfigurationDTO } from "../models/configuration/AreaConfigurationDTO";
import { v4 as uuidv4 } from "uuid";
import { ComponentService } from "../../components/services/component.service";
import { LoggerService } from "../../../shared/services/logger.service";

export interface NewAreaResult {
  newArea: Area;
  newComponents: Component[];
  newAssertions: Assertion[];
}

@Injectable({ providedIn: "root" })
export class AreaService {

  constructor(private assertionService: AssertionService, private componentService: ComponentService, private logger: LoggerService) { }

  async getAreasByLotId(lotId: number): Promise<Area[]> {
    const zones = await db.zone.where("lotId").equals(lotId)
      .toArray();
    return db.area.where("zoneId").anyOf(zones.map(z => z.id))
      .sortBy("sortingOrder");
  }

  async getAreasByIdentifiers(identifiers: string[]): Promise<Area[]> {
    return await db.area
      .where("identifier")
      .anyOf(identifiers)
      .toArray();
  }

  /** Rooms that can be picked for an item: applicable areas that aren't scoped to a single build-quality step. */
  async getSelectableAreasForZones(zoneIds: number[]): Promise<Area[]> {
    return await db.area
      .where("zoneId")
      .anyOf(zoneIds)
      .filter(area => !!area.isApplicable && !area.buildQualityStepScope)
      .toArray();
  }

  public createAreaWithDetails(zones: Zone[], area: Area, components: Component[], assertions: Assertion[], assertionIssues: AssertionIssue[], isCcmInspection: boolean): AreaWithDetails {
    const zoneforArea = zones.find(zone => zone.id === area.zoneId);
    if (!!zoneforArea === false) {
      throw new Error(`Zone with id ${area.zoneId} not found for area ${area.id}`);
    }

    const componentsForArea = components.filter(component => component.areaId === area.id!);
    const componentIds = componentsForArea.map(component => component.id);

    const assertionsForArea = assertions.filter(assertion => componentIds.includes(assertion.componentId!));
    const assertionIdentifiers = assertionsForArea.map(assertion => assertion.identifier);

    const assertionIssuesForArea = assertionIssues.filter(issue => assertionIdentifiers.includes(issue.assertionIdentifier));
    const completedAssertions = this.assertionService.getCompletedAssertions(assertionsForArea, assertionIssuesForArea, isCcmInspection);

    return {
      ...area,
      zone: zoneforArea,
      issues: assertionIssuesForArea,
      components: componentsForArea,
      assertions: assertionsForArea,
      assertionCount: assertionsForArea.length,
      completedAssertions: completedAssertions.length,
      hasIssues: assertionIssuesForArea.length > 0,
      started: assertionIssuesForArea.length > 0 || completedAssertions.length > 0,
      completed: assertionsForArea.every(q =>
        (isCcmInspection ? q.isCcmComplete : q.isComplete) ||
        !q.isApplicable ||
        assertionIssuesForArea.filter(issue => issue.assertionIdentifier === q.identifier).length > 0
      ),
      isCcmInspection
    };
  }

  public async updateAreaNotApplicableStatus(areaIds: number[], areMarkedAreaIdsApplicable: boolean, relatedComponents: Component[], relatedAssertions: Assertion[]): Promise<{ affectedAreaIds: number[], affectedAssertionIds: number[] }> {
    const updatedAt = new Date();

    const markedAreaComponentIds = new Set(
      (relatedComponents ?? [])
        .filter(c => areaIds.includes(c.areaId))
        .map(c => c.id!)
    );
    const updatedAreas: Pick<Area, "id" | "isApplicable">[] = areaIds.map(id => ({
      id,
      isApplicable: areMarkedAreaIdsApplicable
    }));
    const assertionUpdates: { key: number; changes: Pick<Assertion, "isApplicable" | "syncStatus"> }[] = (relatedAssertions ?? [])
      .filter(assertion => assertion.componentId && markedAreaComponentIds.has(assertion.componentId))
      .map(assertion => ({
        key: assertion.id!,
        changes: { isApplicable: areMarkedAreaIdsApplicable, updatedAt, syncStatus: SyncableStatus.Pending }
      }));

    await db.transaction("rw", [db.area, db.assertion, db.media, db.syncStatus], async () => {
      await this.updateAreas(updatedAreas);
      await db.batchBulkOperation("updateAssertions", assertionUpdates, async batchRecords => await db.assertion.bulkUpdate(batchRecords), this.logger);
    });

    return {
      affectedAreaIds: updatedAreas.map(update => update.id!),
      affectedAssertionIds: assertionUpdates.map(update => update.key)
    };
  }

  public async updateAreas(updatedAreas: Partial<Area>[]): Promise<void> {
    const updatedAt = new Date();

    const updates = updatedAreas.map(updatedArea => {
      const { id, ...changes } = updatedArea;
      if (!id) {
        throw new Error("Area id is required for update");
      }

      return {
        key: id,
        changes: { ...changes, updatedAt, syncStatus: SyncableStatus.Pending }
      };
    });

    await db.batchBulkOperation("updateAreas", updates, async batchRecords => await db.area.bulkUpdate(batchRecords), this.logger);
  }

  public async addArea(newArea: Omit<Area, "id" | "updatedAt" | "syncStatus">): Promise<Area> {
    const areaForDb = {
      ...newArea,
      updatedAt: new Date(),
      syncStatus: SyncableStatus.Pending,
    };

    const areaId = await db.area.add(areaForDb);

    return {
      id: areaId,
      ...areaForDb,
    };
  }

  async addAreaToZoneForJob(newAreaName: string, newAreaConfig: AreaConfigurationDTO, zoneId: number, jobId: number) {
    const selectedZoneId = zoneId;
    const { addedArea, addedComponents, addedAssertions } = await db.transaction("rw", [db.area, db.component, db.assertion, db.inspectionJob, db.media, db.syncStatus], async () => {
      const lotId = (await db.inspectionJob
        .where("id")
        .equals(jobId)
        .first())?.lotId ?? 0;

      const newArea: Omit<Area, "id" | "updatedAt" | "syncStatus"> = {
        name: newAreaName,
        zoneId: selectedZoneId,
        isApplicable: true,
        identifier: uuidv4(),
        sortingOrder: newAreaConfig.sortingOrder,
      };

      const addedArea = await this.addArea(newArea);

      const componentsFromConfig = newAreaConfig.components.map(c => ({ ...c, identifier: uuidv4() }));
      const newComponents = componentsFromConfig.map<Omit<Component, "id" | "updatedAt" | "syncStatus">>(c => ({
        name: c.name,
        areaId: addedArea.id!,
        lotId,
        identifier: c.identifier
      }));

      const addedComponents = await this.componentService.addComponents(newComponents);

      const identifierToComponentMap = new Map(addedComponents.map(component => [component.identifier, component]));

      const newAssertionsToAdd = componentsFromConfig
        .flatMap(c => (c.assertions ?? [])
          .map<Omit<Assertion, "id" | "updatedAt" | "syncStatus">>(a => ({
            text: a.text,
            lotId,
            componentId: identifierToComponentMap.get(c.identifier)?.id ?? ((): never => {
              throw new Error(`Component with identifier ${c.identifier} not found`);
            })(),
            isComplete: false,
            isCcmComplete: false,
            isApplicable: true,
            identifier: uuidv4()
          }))
        );

      const addedAssertions = await this.assertionService.addAssertions(newAssertionsToAdd);

      return { addedArea, addedComponents, addedAssertions };
    });


    const newAreaResult: NewAreaResult = {
      newArea: addedArea,
      newComponents: addedComponents,
      newAssertions: addedAssertions
    };

    return newAreaResult;
  }
}

